(function () {
  'use strict';

  const MODAL_SELECTOR = '.xb-popup-modal__product-compare-wrapper';
  const BODY_SELECTOR = '.xb-popup-modal__product-compare-body-content';
  const HEADER_ROW_SELECTOR = '.itmart-compare-header-row';
  const PRODUCT_SELECTORS = [
    '.itmart-compare-header-row [data-product-handle]',
    '.xb-table-compare__product-in-table',
    '.xb-list-product-comparison__product-item',
    '[xb-handle]',
    '[data-product-handle]',
    '[data-xb-handle]'
  ].join(',');
  const SUMMARY_SECTION_SELECTOR = '[data-itmart24-compare-shell]';
  const SECTION_BREAK_SELECTOR = '.itmart24-compare-section-break';
  const contentCache = new Map();

  const PROFILE_MAP = {
    'software': {
      label: 'Software / SaaS',
      summaryFields: ['best_for', 'pricing_model', 'deployment_type', 'integrations', 'support'],
      differenceFields: ['best_for', 'pricing_model', 'deployment_type', 'integrations', 'support', 'refund_policy'],
      tableGroups: [
        { label: 'Key Buying Signals', keys: ['price', 'plan', 'best_for', 'pricing_model', 'deployment_type'] },
        { label: 'Capability & Delivery', keys: ['features', 'integrations', 'compatibility', 'developer_features'] },
        { label: 'Trust & Verification', keys: ['support', 'coverage', 'refund_policy', 'security', 'documentation', 'verification'] }
      ]
    },
    'ai-tools': {
      label: 'AI Tools',
      summaryFields: ['use_case', 'ai_capability', 'pricing_model', 'free_trial', 'support'],
      differenceFields: ['use_case', 'ai_capability', 'pricing_model', 'free_trial', 'security', 'support'],
      tableGroups: [
        { label: 'Use Case & Pricing', keys: ['use_case', 'pricing_model', 'price', 'plan', 'free_trial'] },
        { label: 'Capability & Integrations', keys: ['ai_capability', 'deployment_type', 'integrations', 'developer_features', 'features'] },
        { label: 'Trust & Access', keys: ['security', 'support', 'documentation', 'verification'] }
      ]
    },
    'cloud-services': {
      label: 'Hosting & Cloud',
      summaryFields: ['hosting_type', 'performance_tier', 'billing_cycle', 'support', 'security'],
      differenceFields: ['hosting_type', 'performance_tier', 'billing_cycle', 'deployment_type', 'support', 'refund_policy'],
      tableGroups: [
        { label: 'Plan & Performance', keys: ['hosting_type', 'performance_tier', 'billing_cycle', 'price', 'plan'] },
        { label: 'Operations & Control', keys: ['deployment_type', 'control_panel', 'capacity', 'performance', 'security'] },
        { label: 'Trust & Access', keys: ['support', 'refund_policy', 'documentation', 'verification'] }
      ]
    },
    'developer-tools': {
      label: 'Developer Tools',
      summaryFields: ['best_for', 'developer_features', 'integrations', 'documentation', 'support'],
      differenceFields: ['best_for', 'developer_features', 'integrations', 'documentation', 'pricing_model', 'support'],
      tableGroups: [
        { label: 'Adoption Signals', keys: ['best_for', 'pricing_model', 'price', 'plan'] },
        { label: 'Implementation Depth', keys: ['developer_features', 'integrations', 'compatibility', 'documentation', 'features'] },
        { label: 'Trust & Delivery', keys: ['support', 'security', 'refund_policy', 'verification'] }
      ]
    },
    'cybersecurity': {
      label: 'Cybersecurity',
      summaryFields: ['protection_type', 'security', 'deployment_type', 'support', 'free_trial'],
      differenceFields: ['protection_type', 'security', 'deployment_type', 'support', 'free_trial', 'refund_policy'],
      tableGroups: [
        { label: 'Protection Snapshot', keys: ['protection_type', 'price', 'plan', 'deployment_type'] },
        { label: 'Monitoring & Assurance', keys: ['security', 'coverage', 'features', 'documentation'] },
        { label: 'Trust & Support', keys: ['support', 'refund_policy', 'verification'] }
      ]
    },
    'digital-products': {
      label: 'Digital Products',
      summaryFields: ['best_for', 'pricing_model', 'features', 'support', 'verification'],
      differenceFields: ['best_for', 'pricing_model', 'features', 'support', 'verification'],
      tableGroups: [
        { label: 'Quick Comparison', keys: ['price', 'plan', 'best_for', 'pricing_model', 'features'] },
        { label: 'Research & Evaluation', keys: ['documentation', 'integrations', 'support', 'security'] },
        { label: 'Trust & Access', keys: ['refund_policy', 'verification'] }
      ]
    }
  };

  const FIELD_META = {
    best_for: { label: 'Best for', getItems: (content) => firstNonEmptyItems(content.marketplace?.target_segment_html, content.use_cases_html, content.marketplace?.primary_use_case_html) },
    use_case: { label: 'Use case', getItems: (content) => firstNonEmptyItems(content.marketplace?.primary_use_case_html, content.use_cases_html) },
    pricing_model: { label: 'Pricing model', getItems: (content) => firstNonEmptyItems(content.marketplace?.pricing_model_html, content.pricing_html) },
    deployment_type: { label: 'Deployment type', getItems: (content) => firstNonEmptyItems(content.marketplace?.deployment_model_html) },
    ai_capability: { label: 'AI capability', getItems: (content) => firstNonEmptyItems(content.marketplace?.ai_model_type_html, content.features_html) },
    integrations: { label: 'Integrations', getItems: (content) => firstNonEmptyItems(content.marketplace?.integrations_html) },
    support: { label: 'Support', getItems: (content) => firstNonEmptyItems(content.marketplace?.support_coverage_html, buildVerificationItems(content, 'support')) },
    security: { label: 'Security & privacy', getItems: (content) => firstNonEmptyItems(content.marketplace?.security_compliance_html) },
    free_trial: { label: 'Trial / demo', getItems: (content) => firstNonEmptyItems(content.marketplace?.free_trial_html, content.demo_url ? ['Demo available'] : []) },
    hosting_type: { label: 'Hosting type', getItems: (content) => firstNonEmptyItems(content.marketplace?.hosting_type_html) },
    performance_tier: { label: 'Performance tier', getItems: (content) => firstNonEmptyItems(content.marketplace?.performance_tier_html, content.marketplace?.server_region_html) },
    billing_cycle: { label: 'Billing cycle', getItems: (content) => firstNonEmptyItems(content.marketplace?.billing_cycle_html) },
    control_panel: { label: 'Control panel', getItems: (content) => firstNonEmptyItems(content.marketplace?.control_panel_html) },
    developer_features: { label: 'API / SDK', getItems: (content) => firstNonEmptyItems(content.marketplace?.developer_features_html) },
    documentation: { label: 'Documentation', getItems: (content) => firstNonEmptyItems(content.documentation_url ? ['Documentation available'] : []) },
    refund_policy: { label: 'Refund clarity', getItems: (content) => firstNonEmptyItems(buildVerificationItems(content, 'refund')) },
    verification: { label: 'Verification', getItems: (content) => firstNonEmptyItems(buildTrustItems(content)) },
    features: { label: 'Core features', getItems: (content) => firstNonEmptyItems(content.features_html) },
    protection_type: { label: 'Protection type', getItems: (content) => firstNonEmptyItems(content.marketplace?.primary_use_case_html, content.features_html) }
  };

  let scheduled = null;

  function normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function uniqueValues(values) {
    const seen = new Set();
    const unique = [];
    values.forEach((value) => {
      const normalized = normalizeText(value);
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      unique.push(cleanText(value));
    });
    return unique;
  }

  function extractHandle(node) {
    if (!node) return '';

    const directHandle = node.getAttribute?.('data-product-handle') || node.getAttribute?.('data-xb-handle') || node.getAttribute?.('xb-handle');
    if (directHandle) return directHandle;

    const host = node.closest?.('[data-product-handle], [data-xb-handle], [xb-handle]');
    if (host) {
      return host.getAttribute('data-product-handle') || host.getAttribute('data-xb-handle') || host.getAttribute('xb-handle') || '';
    }

    const link = node.closest?.('a[href*="/products/"]') || node.querySelector?.('a[href*="/products/"]');
    const href = link?.getAttribute('href') || link?.href || '';
    const match = href.match(/\/products\/([^/?#]+)/i);
    return match ? match[1] : '';
  }

  function getModalHandles(modal) {
    const handles = [];
    modal.querySelectorAll(PRODUCT_SELECTORS).forEach((node) => {
      const handle = extractHandle(node);
      if (handle && !handles.includes(handle)) {
        handles.push(handle);
      }
    });
    return handles;
  }

  function fetchCompareContent(handle) {
    if (!handle) return Promise.resolve(null);
    if (contentCache.has(handle)) return contentCache.get(handle);

    const request = fetch(`/products/${handle}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const dataNode = doc.querySelector('[data-itmart-product-compare-content]');
        if (!dataNode) return null;
        try {
          return JSON.parse(dataNode.textContent || '{}');
        } catch (error) {
          return null;
        }
      })
      .catch(() => null);

    contentCache.set(handle, request);
    return request;
  }

  function extractItemsFromHtml(html) {
    if (!html) return [];
    if (Array.isArray(html)) return uniqueValues(html.map((item) => cleanText(item)).filter(Boolean));
    if (typeof html !== 'string') return [];

    const trimmed = html.trim();
    if (!trimmed) return [];

    const doc = new DOMParser().parseFromString(`<div>${trimmed}</div>`, 'text/html');
    const root = doc.body.firstElementChild || doc.body;
    const listItems = Array.from(root.querySelectorAll('li')).map((item) => cleanText(item.textContent)).filter(Boolean);
    if (listItems.length) return uniqueValues(listItems);

    const paragraphItems = Array.from(root.querySelectorAll('p')).map((item) => cleanText(item.textContent)).filter(Boolean);
    if (paragraphItems.length > 1) return uniqueValues(paragraphItems);

    const text = cleanText(root.textContent || '');
    if (!text) return [];
    const parts = text.split(/(?:\s*[ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬Ãƒâ€šÃ‚Â¢|]\s*|\s*,\s*|\s*\/\s*(?=[A-Za-z]))/).map((item) => item.trim()).filter(Boolean);
    return uniqueValues(parts.length > 1 ? parts : [text]);
  }

  function firstNonEmptyItems() {
    for (let index = 0; index < arguments.length; index += 1) {
      const candidate = arguments[index];
      const items = Array.isArray(candidate) ? candidate : extractItemsFromHtml(candidate);
      if (items.length) return items;
    }
    return [];
  }

  function buildTrustItems(content) {
    const items = [];
    if (content?.trust?.verified_product) items.push('Verified product');
    if (content?.trust?.verified_vendor) items.push('Verified vendor');
    if (content?.trust?.support_verified) items.push('Support verified');
    if (content?.trust?.refund_verified) items.push('Refund clarified');
    if (content?.subscription_plan) items.push(`${content.subscription_plan} visibility`);
    return items;
  }

  function buildVerificationItems(content, type) {
    if (!content?.trust) return [];

    if (type === 'support') {
      if (content.trust.support_verification) return [cleanText(content.trust.support_verification)];
      if (content.trust.support_verified) return ['Support verified'];
    }

    if (type === 'refund') {
      if (content.trust.refund_verification) return [cleanText(content.trust.refund_verification)];
      if (content.trust.refund_verified) return ['Refund clarified'];
    }

    return [];
  }

  function getUniqueCategoryLabels(contents) {
    return uniqueValues(contents.map((content) => content?.category_label || content?.category_key || 'Digital Products'));
  }

  function hasMixedCategories(contents) {
    return getUniqueCategoryLabels(contents).length > 1;
  }

  function detectProfile(contents) {
    const counts = new Map();
    contents.forEach((content) => {
      const key = content?.category_key || 'digital-products';
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    let winner = 'digital-products';
    let max = 0;
    counts.forEach((count, key) => {
      if (count > max && PROFILE_MAP[key]) {
        winner = key;
        max = count;
      }
    });

    return PROFILE_MAP[winner] ? winner : 'digital-products';
  }

  function summarizeItems(items) {
    if (!items.length) return 'Not specified';
    const summary = items.slice(0, 3).join(', ');
    return summary.length > 140 ? `${summary.slice(0, 137)}...` : summary;
  }

  function buildChipMarkup(items, emptyText) {
    if (!items.length) return `<span class="itmart24-compare-empty-state">${escapeHtml(emptyText)}</span>`;

    return `<div class="itmart24-compare-chip-list">${items.slice(0, 5).map((item) => `<span class="itmart24-compare-chip">${escapeHtml(item)}</span>`).join('')}</div>`;
  }

  function buildBadgesMarkup(content) {
    const badges = [];
    if (content?.category_label) badges.push({ label: content.category_label, tone: 'accent' });
    if (content?.subscription_plan) badges.push({ label: content.subscription_plan, tone: 'muted' });
    if (content?.trust?.verified_product) badges.push({ label: 'Verified', tone: 'positive' });
    if (content?.trust?.verified_vendor) badges.push({ label: 'Vendor verified', tone: 'positive' });
    if (content?.trust?.support_verified) badges.push({ label: 'Support', tone: 'accent' });
    if (content?.trust?.refund_verified) badges.push({ label: 'Refund clarity', tone: 'warning' });

    return badges.map((badge) => `<span class="itmart24-compare-badge" data-tone="${badge.tone}">${escapeHtml(badge.label)}</span>`).join('');
  }

  function buildLinksMarkup(content) {
    const links = [
      { href: content?.product_url || `/products/${content?.handle || ''}`, label: 'View Product', tone: 'primary' },
      { href: content?.official_url, label: 'Visit Official Website', tone: 'secondary', external: true },
      { href: content?.demo_url, label: 'Open Demo', tone: 'secondary', external: true },
      { href: content?.documentation_url, label: 'Documentation', tone: 'secondary', external: true }
    ].filter((link) => link.href);

    return links.slice(0, 4).map((link) => {
      const className = link.tone === 'primary' ? 'itmart-compare-hero-card__cta' : 'itmart24-compare-link-pill';
      const rel = link.external ? ' rel="noopener noreferrer" target="_blank"' : '';
      return `<a class="${className}" href="${escapeHtml(link.href)}"${rel}>${escapeHtml(link.label)}</a>`;
    }).join('');
  }

  function buildSummaryCard(content, profileKey) {
    const profile = PROFILE_MAP[profileKey] || PROFILE_MAP['digital-products'];
    const detailMarkup = profile.summaryFields.map((fieldKey) => {
      const field = FIELD_META[fieldKey];
      if (!field) return '';
      const items = field.getItems(content);
      return `
        <div class="itmart24-compare-detail">
          <span class="itmart24-compare-detail__label">${escapeHtml(field.label)}</span>
          <div class="itmart24-compare-detail__value">${buildChipMarkup(items, 'Not specified')}</div>
        </div>
      `;
    }).join('');

    return `
      <article class="itmart24-compare-summary-card">
        <div class="itmart24-compare-summary-card__title-row">
          <div>
            <h3 class="itmart24-compare-summary-card__title">${escapeHtml(content?.title || content?.handle || 'Product')}</h3>
            <p class="itmart24-compare-summary-card__description">${escapeHtml(content?.short_description || 'Quick decision summary not specified.')}</p>
          </div>
        </div>
        <div class="itmart24-compare-summary-card__badges">${buildBadgesMarkup(content)}</div>
        <div class="itmart24-compare-detail-list">${detailMarkup}</div>
        <div class="itmart24-compare-summary-card__links">${buildLinksMarkup(content)}</div>
      </article>
    `;
  }

  function buildDifferencesMarkup(contents, profileKey) {
    const profile = PROFILE_MAP[profileKey] || PROFILE_MAP['digital-products'];
    const cards = [];

    profile.differenceFields.forEach((fieldKey) => {
      const field = FIELD_META[fieldKey];
      if (!field) return;
      const values = contents.map((content) => ({
        title: content?.title || content?.handle || 'Product',
        summary: summarizeItems(field.getItems(content))
      }));
      const distinct = uniqueValues(values.map((value) => value.summary).filter((value) => value && normalizeText(value) !== 'not specified'));
      if (distinct.length < 2) return;

      cards.push(`
        <article class="itmart24-compare-diff-card">
          <span class="itmart24-compare-diff-card__label">${escapeHtml(field.label)}</span>
          <ul class="itmart24-compare-diff-list">
            ${values.map((value) => `
              <li>
                <span>${escapeHtml(value.title)}</span>
                <strong>${escapeHtml(value.summary)}</strong>
              </li>
            `).join('')}
          </ul>
        </article>
      `);
    });

    if (!cards.length) {
      return '';
    }

    return `
      <section class="itmart24-compare-section itmart24-compare-section--differences">
        <div class="itmart24-compare-section__head">
          <div>
            <span class="itmart24-compare-section__eyebrow">Key Differences</span>
            <h3 class="itmart24-compare-section__title">Major differences surfaced first</h3>
            <p class="itmart24-compare-section__subtitle">The strongest differences are grouped here so buyers can scan the shortlist in a few seconds.</p>
          </div>
        </div>
        <div class="itmart24-compare-diff-grid">${cards.join('')}</div>
      </section>
    `;
  }

  function buildSummaryShell(contents, profileKey) {
    const profile = PROFILE_MAP[profileKey] || PROFILE_MAP['digital-products'];
    const differenceMarkup = buildDifferencesMarkup(contents, profileKey);

    return `
      <div class="itmart24-compare-shell" data-itmart24-compare-shell>
        ${differenceMarkup}
        <section class="itmart24-compare-section itmart24-compare-section--summary">
          <div class="itmart24-compare-section__head">
            <div>
              <span class="itmart24-compare-section__eyebrow">${escapeHtml(profile.label)}</span>
              <h3 class="itmart24-compare-section__title">Category-aware shortlist summary</h3>
              <p class="itmart24-compare-section__subtitle">Review the buying signals that matter most for this category before diving into the full comparison table.</p>
            </div>
          </div>
          <div class="itmart24-compare-summary-grid">${contents.map((content) => buildSummaryCard(content, profileKey)).join('')}</div>
        </section>
      </div>
    `;
  }

  function buildHeroMeta(content) {
    const bits = [];
    if (content?.category_label) bits.push(`<span class="itmart24-compare-badge" data-tone="accent">${escapeHtml(content.category_label)}</span>`);
    if (content?.subscription_plan) bits.push(`<span class="itmart24-compare-badge" data-tone="muted">${escapeHtml(content.subscription_plan)}</span>`);
    if (content?.trust?.verified_product) bits.push('<span class="itmart24-compare-badge" data-tone="positive">Verified</span>');
    if (content?.trust?.verified_vendor) bits.push('<span class="itmart24-compare-badge" data-tone="positive">Vendor verified</span>');
    return bits.join('');
  }

  function enhanceHeroCards(modal, contents) {
    const contentMap = new Map(contents.map((content) => [content.handle, content]));

    modal.querySelectorAll('.itmart-compare-hero-card').forEach((card) => {
      const handle = extractHandle(card) || card.getAttribute('data-product-handle') || '';
      const content = contentMap.get(handle);
      if (!content) return;

      const body = card.querySelector('.itmart-compare-hero-card__body');
      if (!body) return;

      let meta = body.querySelector('.itmart24-compare-hero-meta');
      if (!meta) {
        meta = document.createElement('div');
        meta.className = 'itmart24-compare-hero-meta';
        body.appendChild(meta);
      }
      meta.innerHTML = buildHeroMeta(content);

      let actions = body.parentElement.querySelector('.itmart-compare-hero-card__actions');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'itmart-compare-hero-card__actions';
        body.parentElement.appendChild(actions);
      }
      actions.innerHTML = buildLinksMarkup(content);
    });
  }

  function inferFieldKey(row) {
    const preset = row.getAttribute('data-itmart-compare-field');
    if (preset && preset !== 'custom') return preset;

    const heading = row.querySelector('.xb-table-compare__cell-heading');
    const label = normalizeText(heading?.textContent || '');

    const checks = [
      ['best_for', /best for|use case|target segment/],
      ['pricing_model', /pricing model/],
      ['billing_cycle', /billing cycle/],
      ['deployment_type', /deployment|hosting|server region/],
      ['ai_capability', /ai model|model type|ai capability/],
      ['integrations', /integrations?/],
      ['support', /support|sla/],
      ['security', /security|compliance|privacy/],
      ['hosting_type', /hosting type/],
      ['performance_tier', /performance tier|uptime|bandwidth|storage|cpu|ram/],
      ['control_panel', /control panel/],
      ['developer_features', /developer|sdk|api|webhook|framework/],
      ['documentation', /documentation|docs|guide|demo|official/],
      ['refund_policy', /refund|return/],
      ['features', /features|highlights/],
      ['verification', /verified|vendor verification/],
      ['price', /price|pricing|cost/],
      ['plan', /plan|tier|edition/]
    ];

    for (const [key, pattern] of checks) {
      if (pattern.test(label)) return key;
    }

    return 'custom';
  }

  function formatValueText(value) {
    const normalized = normalizeText(value);
    if (!normalized) return '';
    if (['yes', 'available', 'included', 'true', 'supported', 'active'].includes(normalized)) {
      return '<span class="itmart24-compare-badge" data-tone="positive">Available</span>';
    }
    if (['no', 'not available', 'unavailable', 'false', 'excluded', 'not supported', 'n/a', 'na'].includes(normalized)) {
      return '<span class="itmart24-compare-badge" data-tone="danger">Not available</span>';
    }
    return '';
  }

  function enhanceCell(cell) {
    if (!cell || cell.querySelector('.itmart-compare-hero-card, .itmart-compare-rich-card, .itmart-compare-pros-cons')) {
      return;
    }

    if (cell.querySelector('.itmart24-compare-cell-clamp, .itmart24-compare-list, .itmart24-compare-chip-list, .itmart24-compare-cell-links, .itmart24-compare-cell-stack, .itmart24-compare-empty-state')) {
      return;
    }

    const signature = cleanText(cell.textContent);
    if (cell.dataset.itmart24Signature === signature) return;
    cell.dataset.itmart24Signature = signature;

    const links = Array.from(cell.querySelectorAll('a[href]'));
    if (links.length) {
      cell.innerHTML = `<div class="itmart24-compare-cell-links">${links.map((link) => {
        const href = link.getAttribute('href') || '#';
        const label = cleanText(link.textContent) || 'Open link';
        const external = /^https?:/i.test(href) ? ' rel="noopener noreferrer" target="_blank"' : '';
        return `<a class="itmart24-compare-link-pill" href="${escapeHtml(href)}"${external}>${escapeHtml(label)}</a>`;
      }).join('')}</div>`;
      return;
    }

    const rawHtml = cell.innerHTML;
    const items = extractItemsFromHtml(rawHtml);
    if (!items.length) {
      cell.innerHTML = '<span class="itmart24-compare-empty-state">Not specified</span>';
      return;
    }

    const booleanMarkup = items.length === 1 ? formatValueText(items[0]) : '';
    if (booleanMarkup) {
      cell.innerHTML = booleanMarkup;
      return;
    }

    if (items.length > 1 && items.every((item) => item.length <= 36)) {
      cell.innerHTML = buildChipMarkup(items, 'Not specified');
      return;
    }

    if (items.length > 1) {
      cell.innerHTML = `<ul class="itmart24-compare-list">${items.slice(0, 6).map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
      return;
    }

    const text = items[0];
    if (text.length > 180) {
      cell.innerHTML = `
        <div class="itmart24-compare-cell-clamp" data-expanded="false">
          <div class="itmart24-compare-cell-clamp__text">${escapeHtml(text)}</div>
          <button type="button" class="itmart24-compare-cell-clamp__toggle" aria-expanded="false">Read more</button>
        </div>
      `;
      return;
    }

    cell.innerHTML = `<div class="itmart24-compare-cell-stack"><span>${escapeHtml(text)}</span></div>`;
  }

  function groupTableRows(modal, profileKey) {
    modal.querySelectorAll(SECTION_BREAK_SELECTOR).forEach((node) => node.remove());

    const body = modal.querySelector(BODY_SELECTOR);
    if (!body) return;

    const profile = PROFILE_MAP[profileKey] || PROFILE_MAP['digital-products'];
    const rows = Array.from(body.querySelectorAll('[data-itmart-compare-field]')).filter((row) => !row.classList.contains('itmart-is-hidden') && row.getAttribute('data-itmart-custom-row') !== 'product_overview');
    rows.forEach((row) => {
      row.dataset.itmart24FieldKey = inferFieldKey(row);
      row.querySelectorAll(':scope > *:not(.xb-table-compare__cell-heading)').forEach(enhanceCell);
    });

    profile.tableGroups.forEach((group) => {
      const row = rows.find((candidate) => group.keys.includes(candidate.dataset.itmart24FieldKey));
      if (!row) return;
      const marker = document.createElement('div');
      marker.className = 'itmart24-compare-section-break';
      marker.innerHTML = `<span>${escapeHtml(group.label)}</span>`;
      body.insertBefore(marker, row);
    });
  }

  function mountSummarySections(modal, contents, profileKey) {
    const body = modal.querySelector(BODY_SELECTOR);
    const headerRow = body?.querySelector(HEADER_ROW_SELECTOR);
    if (!body) return;

    body.querySelectorAll(SUMMARY_SECTION_SELECTOR).forEach((node) => node.remove());

    const shell = document.createElement('div');
    shell.innerHTML = buildSummaryShell(contents, profileKey);
    const fragment = shell.firstElementChild;
    if (!fragment) return;

    if (headerRow && headerRow.nextSibling) {
      body.insertBefore(fragment, headerRow.nextSibling);
    } else {
      body.insertBefore(fragment, body.firstElementChild || null);
    }
  }

  async function enhanceModal(modal) {
    const handles = getModalHandles(modal);
    if (handles.length < 2) return;

    const contents = (await Promise.all(handles.map(fetchCompareContent))).filter(Boolean);
    if (contents.length < 2) return;

    const profileKey = detectProfile(contents);
    mountSummarySections(modal, contents, profileKey);
    enhanceHeroCards(modal, contents);
    groupTableRows(modal, profileKey);
  }

  function scheduleEnhancements() {
    if (scheduled) return;
    scheduled = window.setTimeout(() => {
      scheduled = null;
      document.querySelectorAll(MODAL_SELECTOR).forEach((modal) => {
        enhanceModal(modal);
      });
    }, 120);
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.itmart24-compare-cell-clamp__toggle');
    if (!toggle) return;
    const clamp = toggle.closest('.itmart24-compare-cell-clamp');
    if (!clamp) return;
    const expanded = clamp.getAttribute('data-expanded') === 'true';
    clamp.setAttribute('data-expanded', expanded ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    toggle.textContent = expanded ? 'Read more' : 'Show less';
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;
      const hasCompareNode = Array.from(mutation.addedNodes).some((node) => {
        return node.nodeType === 1 && (
          node.matches?.(MODAL_SELECTOR) ||
          node.querySelector?.(MODAL_SELECTOR) ||
          node.matches?.('.xb-table-compare__product-in-table, .itmart-compare-header-row, .xb-popup-modal__product-compare-body-content') ||
          node.querySelector?.('.xb-table-compare__product-in-table, .itmart-compare-header-row, .xb-popup-modal__product-compare-body-content')
        );
      });

      if (hasCompareNode) {
        scheduleEnhancements();
        break;
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  scheduleEnhancements();
  window.addEventListener('load', scheduleEnhancements, { once: true });
})();